/**
 * useScn* — Supplier S3 (change-notification workflow).
 *
 * GET  /api/v1/supplier-change-notifications              → SCNs + summary.
 * POST /api/v1/supplier-change-notifications/{id}/transition { to } → advance
 *      the workflow (RECEIVED → UNDER_ASSESSMENT → APPROVED|REJECTED).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type ScnStatus = 'RECEIVED' | 'UNDER_ASSESSMENT' | 'APPROVED' | 'REJECTED';

export interface Scn {
  scn_id: string;
  supplier_id: string;
  supplier_name: string;
  change_type: string;
  title: string;
  impact: 'MAJOR' | 'MINOR';
  status: ScnStatus;
  raised_at_utc: string;
  sla_due_at_utc: string;
  requires_requal: boolean;
}
export interface ScnResponse {
  items: Scn[];
  summary: { total: number; received: number; under_assessment: number; approved: number; rejected: number };
}

export function useScnQuery(): UseQueryResult<ScnResponse> {
  return useQuery<ScnResponse>({
    queryKey: ['suppliers', 'change-notifications'],
    queryFn: ({ signal }) => apiFetch<ScnResponse>('/api/v1/supplier-change-notifications', { signal }),
    staleTime: 30_000,
  });
}

export function useScnTransition(): UseMutationResult<{ scn_id: string; status: ScnStatus }, Error, { scnId: string; to: ScnStatus }> {
  const qc = useQueryClient();
  return useMutation<{ scn_id: string; status: ScnStatus }, Error, { scnId: string; to: ScnStatus }>({
    mutationFn: ({ scnId, to }) =>
      apiFetch(`/api/v1/supplier-change-notifications/${encodeURIComponent(scnId)}/transition`, {
        method: 'POST', body: { to },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['suppliers', 'change-notifications'] }),
  });
}
